import gamesRaw from './games.json';
import soppelRaw from './levels/soppelbilen.json';
import snoRaw from './levels/snobroyteren.json';
import brannRaw from './levels/brannbilen.json';
import {
  BrannLevelsFileSchema,
  GamesManifestSchema,
  SnoLevelsFileSchema,
  SoppelLevelsFileSchema,
  type AnyLevel,
  type BrannLevel,
  type GameId,
  type GamesManifest,
  type SnoLevel,
  type FinishPoint,
  type SoppelLevel,
  type StartPoint,
} from './schemas';

function failLoud(label: string, error: unknown): never {
  const detail =
    error instanceof Error ? error.message : typeof error === 'string' ? error : JSON.stringify(error);
  const message = `[enklespill] Invalid ${label}: ${detail}`;
  // Fail loudly in development — never silently accept bad level data.
  // eslint-disable-next-line no-console
  console.error(message, error);
  throw new Error(message);
}

export const gamesManifest: GamesManifest = (() => {
  const result = GamesManifestSchema.safeParse(gamesRaw);
  if (!result.success) failLoud('games.json', result.error);
  return result.data;
})();

export const soppelLevelsFile = (() => {
  const result = SoppelLevelsFileSchema.safeParse(soppelRaw);
  if (!result.success) failLoud('levels/soppelbilen.json', result.error);
  return result.data;
})();

export const snoLevelsFile = (() => {
  const result = SnoLevelsFileSchema.safeParse(snoRaw);
  if (!result.success) failLoud('levels/snobroyteren.json', result.error);
  return result.data;
})();

export const brannLevelsFile = (() => {
  const result = BrannLevelsFileSchema.safeParse(brannRaw);
  if (!result.success) failLoud('levels/brannbilen.json', result.error);
  return result.data;
})();

export const designReadyGameIds: GameId[] = ['soppelbilen', 'snobroyteren', 'brannbilen'];

export function getLevelsForGame(gameId: GameId): AnyLevel[] {
  switch (gameId) {
    case 'soppelbilen':
      return soppelLevelsFile.levels;
    case 'snobroyteren':
      return snoLevelsFile.levels;
    case 'brannbilen':
      return brannLevelsFile.levels;
    default:
      return [];
  }
}

export function getLevelById(gameId: GameId, levelId: string): AnyLevel | undefined {
  return getLevelsForGame(gameId).find((level) => level.id === levelId);
}

export function isSoppelLevel(level: AnyLevel): level is SoppelLevel {
  return 'mode' in level && 'stops' in level;
}

/** Game defaults with per-level overrides for Søppelbilen start/finish. */
export function resolveSoppelEndpoints(level: SoppelLevel): {
  start: StartPoint;
  finish: FinishPoint;
} {
  return {
    start: level.start ?? soppelLevelsFile.start,
    finish: level.finish ?? soppelLevelsFile.finish,
  };
}

export function isSnoLevel(level: AnyLevel): level is SnoLevel {
  return 'plow' in level && 'goal' in level;
}

export function isBrannLevel(level: AnyLevel): level is BrannLevel {
  return 'tasks' in level && 'drive' in level;
}

export function getDesignReadyGames() {
  return gamesManifest.games
    .filter((game) => game.status === 'design-ready' && game.levelFile)
    .sort((a, b) => a.priority - b.priority);
}

export function getPlannedGames() {
  return gamesManifest.games
    .filter((game) => game.status === 'planned')
    .sort((a, b) => a.priority - b.priority);
}
